export const AB_TEST_BUCKET_EVENT_STORAGE_KEY = 'lp-ab-test-pageview'

/**
 * Determine which group a user is in for a given A/B test split with the given device id.
 *
 * **NOTE:** this isn't related to GrowthBook. It's a way for us to do our own basic A/B testing
 * for things like Dato CMS.
 *
 * @param aSplitPercentage - the percentage of users in group A
 * @param bSplitPercentage - the percentage of users in group B
 * @param deviceId - the device id to bucket (usually a GUID like the growthbook device ID, but can be any string)
 * @returns 0 for group A, 1 for group B
 */
export function getABTestBucket(
  aSplitPercentage: number,
  bSplitPercentage: number,
  deviceId: string,
) {
  const gcd = getGreatestCommonDenominator(aSplitPercentage, bSplitPercentage)
  const aWeight = aSplitPercentage / gcd
  const bWeight = bSplitPercentage / gcd
  const totalWeight = aWeight + bWeight

  // a simple hash function to get a 0 or 1 number from the deviceId
  const hash = deviceId
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0)

  // group will be between 0 and totalWeight (e.g. a split of 33/66 will be 0, 1, or 2)
  const group = hash % totalWeight
  return group < aWeight ? 0 : 1
}

/**
 * Get the greatest common denominator of two numbers.
 *
 * @param a - first number
 * @param b - second number
 * @returns the greatest common denominator of a and b (e.g. 20 & 80 => 20)
 */
export function getGreatestCommonDenominator(a: number, b: number) {
  if (!b) return a
  return getGreatestCommonDenominator(b, a % b)
}

/**
 * Gets the LP variant from local storage.
 *
 * @returns the variant of the A/B test (A, B or undefined if no local storage item was found)
 */
export function getLPVariantFromLocalStorage(): 'A' | 'B' | undefined {
  const stored = JSON.parse(
    window.localStorage.getItem(AB_TEST_BUCKET_EVENT_STORAGE_KEY) || 'null',
  )
  return stored && stored.variant ? (stored.variant as 'A' | 'B') : undefined
}
